import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { GRADOS, PERIODOS, VALORACIONES } from '../../constants';

// --- Componente para la Vista de Calificación Individual ---
function StudentGradingView({ student, user, selectedPeriodo, onBack, achievementsByDimension }) {
    const [grades, setGrades] = useState({});
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // Carga las calificaciones existentes del estudiante al montar el componente.
        const fetchGrades = async () => {
            const gradeDocRef = doc(db, "calificaciones", `${student.id}_${selectedPeriodo}`);
            const gradeSnap = await getDoc(gradeDocRef);
            if (gradeSnap.exists()) {
                setGrades(gradeSnap.data().calificaciones || {});
            }
        };
        fetchGrades();
    }, [student.id, selectedPeriodo]);

    const handleGradeChange = (achievementId, value) => {
        setGrades(prev => ({ ...prev, [achievementId]: value }));
    };

    const saveGrades = async () => {
        setMessage('');
        setIsSubmitting(true);
        try {
            const gradeDocRef = doc(db, "calificaciones", `${student.id}_${selectedPeriodo}`);
            await setDoc(gradeDocRef, {
                estudianteId: student.id,
                periodo: Number(selectedPeriodo),
                profesorId: user.uid,
                calificaciones: grades
            });
            setMessage(`Calificaciones de ${student.nombreCompleto} guardadas exitosamente.`);
        } catch (error) {
            setMessage('Error al guardar: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="glass-card p-8 rounded-2xl animate-fade-in">
            <div className="flex items-center justify-between mb-8">
                <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-primary-600 transition-colors font-medium">
                    &larr; Volver
                </button>
                <div className="text-right">
                    <h3 className="text-2xl font-bold text-slate-800">
                        Calificando a: <span className="text-primary-600">{student.nombreCompleto}</span>
                    </h3>
                    <p className="text-slate-500 font-medium">Período {selectedPeriodo}</p>
                </div>
            </div>

            <div className="space-y-8">
                {Object.entries(achievementsByDimension).map(([dimension, achievements]) => (
                    <div key={dimension} className="rounded-xl overflow-hidden border border-slate-200/50 shadow-sm">
                        <h4 className="text-lg font-bold bg-slate-50/80 p-4 border-b border-slate-100 text-slate-700 uppercase tracking-wide">
                            {dimension}
                        </h4>
                        <table className="w-full bg-white/50">
                            <thead>
                                <tr className="text-left text-xs uppercase text-slate-400 bg-slate-50/30">
                                    <th className="py-3 px-6 font-semibold">Logro / Aspecto a Evaluar</th>
                                    <th className="py-3 px-6 font-semibold text-center w-48">Valoración</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {achievements.map(ach => (
                                    <tr key={ach.id} className="hover:bg-primary-50/10 transition-colors">
                                        <td className="py-4 px-6 text-slate-700 text-sm leading-relaxed">{ach.descripcion}</td>
                                        <td className="py-4 px-6">
                                            <select
                                                value={grades[ach.id] || ''}
                                                onChange={(e) => handleGradeChange(ach.id, e.target.value)}
                                                className="glass-input w-full px-3 py-2 rounded-lg text-sm text-center font-medium"
                                            >
                                                <option value="">-</option>
                                                {Object.keys(VALORACIONES).map(key => (
                                                    <option key={key} value={key}>{key}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center justify-end gap-4">
                {message && <div className="text-green-600 font-medium animate-fade-in">{message}</div>}
                <button onClick={saveGrades} disabled={isSubmitting} className="btn-primary px-8 py-3 rounded-xl font-bold flex items-center gap-2">
                    {isSubmitting ? 'Guardando...' : 'Guardar Calificaciones'}
                </button>
            </div>
        </div>
    );
}


// --- Componente Principal de la Planilla de Calificaciones ---
export default function GradeSheet({ user, currentYear }) {
    const [students, setStudents] = useState([]);
    const [achievementsByDimension, setAchievementsByDimension] = useState({});
    const [selectedGrado, setSelectedGrado] = useState(GRADOS[0]);
    const [selectedPeriodo, setSelectedPeriodo] = useState(PERIODOS[0]);
    const [selectedStudent, setSelectedStudent] = useState(null); // Para saber a qué estudiante calificar
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        if (!user || !currentYear) return;
        setLoading(true);
        setError('');
        setSelectedStudent(null); // Resetea la vista al cambiar de filtro

        try {
            // 1. Obtener estudiantes
            const studentQuery = query(collection(db, "estudiantes"), where("profesorId", "==", user.uid), where("grado", "==", selectedGrado));
            const studentSnap = await getDocs(studentQuery);
            const studentList = studentSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(s => (s.year || 2025) === currentYear); // Client-side filtering for Year

            setStudents(studentList);

            // 2. Obtener logros y agruparlos por dimensión
            const achievementQuery = query(collection(db, "logros"), where("profesorId", "==", user.uid), where("grado", "==", selectedGrado), where("periodo", "==", Number(selectedPeriodo)));
            const achievementSnap = await getDocs(achievementQuery);
            const groupedAchievements = {};
            achievementSnap.docs.forEach(doc => {
                const data = { id: doc.id, ...doc.data() };
                const achYear = data.year || 2025;

                if (achYear === currentYear) { // Filter by Year
                    if (!groupedAchievements[data.dimension]) {
                        groupedAchievements[data.dimension] = [];
                    }
                    groupedAchievements[data.dimension].push(data);
                }
            });
            setAchievementsByDimension(groupedAchievements);

        } catch (err) {
            setError('Error al cargar los datos: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [user, currentYear, selectedGrado, selectedPeriodo]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (selectedStudent) {
        return (
            <StudentGradingView
                student={selectedStudent}
                user={user}
                selectedPeriodo={selectedPeriodo}
                onBack={() => setSelectedStudent(null)}
                achievementsByDimension={achievementsByDimension}
            />
        );
    }

    return (
        <div className="glass-card p-8 rounded-2xl animate-fade-in">
            <h2 className="text-2xl font-bold mb-6 text-slate-800">Planilla de Calificaciones</h2>
            <div className="flex flex-wrap gap-6 mb-8 p-6 bg-slate-50/50 rounded-xl border border-slate-100">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-slate-600 font-semibold mb-2">Grado</label>
                    <select value={selectedGrado} onChange={(e) => setSelectedGrado(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                        {GRADOS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-slate-600 font-semibold mb-2">Período</label>
                    <select value={selectedPeriodo} onChange={(e) => setSelectedPeriodo(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                        {PERIODOS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </div>
            {error && <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 font-medium">{error}</div>}

            {loading ? <div className="text-center p-8 text-slate-500">Cargando datos...</div> : (
                <div className="overflow-hidden rounded-xl border border-slate-200/50">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/50">
                            <tr>
                                <th className="py-4 px-6 text-slate-600 font-bold uppercase text-xs tracking-wider">Nombre del Estudiante</th>
                                <th className="py-4 px-6 text-center text-slate-600 font-bold uppercase text-xs tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white/50">
                            {students.length > 0 ? students.map(student => (
                                <tr key={student.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="py-4 px-6 font-medium text-slate-700">{student.nombreCompleto}</td>
                                    <td className="py-4 px-6 text-center">
                                        <button onClick={() => setSelectedStudent(student)} className="text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md">
                                            Calificar
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="2" className="py-8 px-6 text-center text-slate-400 italic">No hay estudiantes registrados en este grado.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
